import React from 'react';

/**
 * Custom BBCode parser for vBulletin tags:
 * [table], [tbl], [tr], [td], [th] -> HTML table
 * [COLOR=...], [SIZE=...], [FONT=...], [b], [i], [u], [url], [quote], [div]
 */
export const renderFormattedBBCode = (text) => {
  if (!text) return '';

  let html = text;

  // Remove lingering BBCode closing artifacts
  html = html.replace(/\[\/(COLOR|SIZE|FONT|SIZE|div)\]/gi, '');

  // Color BBCode: [COLOR=#ff0000]text[/COLOR] or [COLOR=red]text[/COLOR]
  html = html.replace(/\[COLOR=([^\]]+)\](.*?)\[\/COLOR\]/gis, (match, color, content) => {
    // Guard against AGENTS.md black rule (#000000 -> #1A237E)
    let safeColor = color.trim().toLowerCase();
    if (safeColor === '#000000' || safeColor === 'black') {
      safeColor = '#1A237E';
    }
    return `<span style="color: ${safeColor}">${content}</span>`;
  });

  // Font BBCode: [FONT=...]text[/FONT]
  html = html.replace(/\[FONT=([^\]]+)\](.*?)\[\/FONT\]/gis, '$2');

  // Size BBCode: [SIZE=...]text[/SIZE]
  html = html.replace(/\[SIZE=([^\]]+)\](.*?)\[\/SIZE\]/gis, '$2');

  // YouTube BBCode: [yvid]ORI1ZC0Zn5k[/yvid] or [youtube]ORI1ZC0Zn5k[/youtube]
  html = html.replace(/\[(yvid|youtube)\]([a-zA-Z0-9_\-]+)\[\/(yvid|youtube)\]/gis, (match, tag, id) => {
    return `<div class="relative w-full max-w-2xl mx-auto my-4 aspect-video rounded-xl overflow-hidden shadow-lg border border-[#D4AF37]/50">
      <iframe class="w-full h-full" src="https://www.youtube-nocookie.com/embed/${id}?rel=0" title="YouTube Video Player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
    </div>`;
  });

  // PDF BBCode: [pdf]url[/pdf]
  html = html.replace(/\[pdf\](.*?)\[\/pdf\]/gis, (match, url) => {
    const cleanUrl = url.trim();
    return `<div class="my-4 rounded-xl border border-[#D4AF37]/50 overflow-hidden bg-[#FAF6EE] p-3 shadow-md">
      <div class="flex items-center justify-between mb-2">
        <span class="font-bold text-[#1A237E] flex items-center gap-2">📄 Document Preview (PDF)</span>
        <a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-xs bg-[#CC6600] text-white font-bold px-3 py-1 rounded-md hover:bg-[#E65100]">Download PDF</a>
      </div>
      <iframe src="${cleanUrl}#toolbar=1" class="w-full h-[600px] rounded-lg border border-amber-200" title="PDF Viewer"></iframe>
    </div>`;
  });

  // MP3 Audio Player: [mp3]url[/mp3] or [audio]url[/audio]
  html = html.replace(/\[(mp3|audio)\](.*?)\[\/(mp3|audio)\]/gis, (match, tag, url) => {
    const cleanUrl = url.trim();
    return `<div class="my-3 p-3 bg-gradient-to-r from-[#FAF6EE] to-[#F5EDDA] border border-[#D4AF37]/50 rounded-xl shadow-xs flex flex-col gap-2">
      <div class="flex items-center gap-2 text-xs font-bold text-[#1A237E]">
        <span>🎵 Audio Track</span>
      </div>
      <audio controls class="w-full h-10 rounded-lg">
        <source src="${cleanUrl}" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>`;
  });

  // HTML5 Video: [vdo]url[/vdo] or [video]url[/video]
  html = html.replace(/\[(vdo|video)\](.*?)\[\/(vdo|video)\]/gis, (match, tag, url) => {
    const cleanUrl = url.trim();
    return `<div class="my-4 rounded-xl overflow-hidden shadow-lg border border-[#D4AF37]/50 max-w-2xl mx-auto bg-black">
      <video controls class="w-full h-auto max-h-[500px]">
        <source src="${cleanUrl}" />
        Your browser does not support the video tag.
      </video>
    </div>`;
  });

  // Highlight BBCode: [hl=Yellow]text[/hl] or [hl]text[/hl]
  html = html.replace(/\[hl(?:=([^\]]+))?\](.*?)\[\/hl\]/gis, (match, bg, content) => {
    const bgColor = bg ? bg.trim() : '#FEF08A';
    return `<mark style="background-color: ${bgColor}" class="px-1.5 py-0.5 rounded font-semibold text-[#1A237E]">${content}</mark>`;
  });

  // Marquee / Moving text: [move]text[/move]
  html = html.replace(/\[move\](.*?)\[\/move\]/gis, '<marquee class="my-2 p-2 bg-[#FAF6EE] border border-[#D4AF37]/40 rounded-lg font-medium text-[#1A237E]">$1</marquee>');

  // Rainbow / Text Fade: [fade]text[/fade]
  html = html.replace(/\[fade\](.*?)\[\/fade\]/gis, '<span class="bg-gradient-to-r from-red-600 via-yellow-500 to-indigo-600 bg-clip-text text-transparent font-bold">$1</span>');

  // Head Span: [hdspan]text[/hdspan]
  html = html.replace(/\[hdspan\](.*?)\[\/hdspan\]/gis, '<span class="block text-2xl font-serif font-bold text-[#4A148C] my-2 leading-relaxed">$1</span>');

  // Image Center & Image Left
  html = html.replace(/\[imgc\](.*?)\[\/imgc\]/gis, '<div class="text-center my-3"><img src="$1" alt="Forum Image" class="max-w-full h-auto rounded-xl border border-[#D4AF37]/40 shadow-md inline-block max-h-[500px] object-contain" /></div>');
  html = html.replace(/\[imgl\](.*?)\[\/imgl\]/gis, '<img src="$1" alt="Forum Image" class="float-left mr-4 mb-3 max-w-[280px] h-auto rounded-xl border border-[#D4AF37]/40 shadow-md" />');

  // Div BBCode: [div]text[/div]
  html = html.replace(/\[div\](.*?)\[\/div\]/gis, '<div class="my-3 p-4 bg-[#FAF6EE] border border-[#D4AF37]/40 rounded-xl shadow-xs text-[#1A237E] leading-relaxed">$1</div>');

  // Table BBCode: [tbl] or [table] -> HTML Table with Parchment Traditional Design
  const processTables = (str) => {
    return str.replace(/\[(tbl|table)\](.*?)\[\/(tbl|table)\]/gis, (match, tag, inner) => {
      let rows = inner.replace(/\[tr\](.*?)\[\/tr\]/gis, (rMatch, trContent) => {
        let cols = trContent.replace(/\[(td|th)\](.*?)\[\/(td|th)\]/gis, (cMatch, colTag, cellContent) => {
          const isHeader = colTag.toLowerCase() === 'th';
          if (isHeader) {
            return `<th class="bg-[#1A237E] text-white font-bold px-3 py-2 text-left border border-indigo-900 text-xs">${cellContent}</th>`;
          }
          return `<td class="px-3 py-2 border border-[#D4AF37]/30 text-xs text-[#2D5A1E] font-medium">${cellContent}</td>`;
        });
        return `<tr class="hover:bg-amber-50/50 transition-colors">${cols}</tr>`;
      });

      return `<div class="overflow-x-auto my-4 rounded-xl border border-[#D4AF37]/40 shadow-xs">
        <table class="w-full text-left border-collapse bg-white">
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    });
  };

  html = processTables(html);

  // vBulletin JSON attachment tags parser: [ATTACH=JSON]{...data-attachmentid":49922}[/ATTACH]
  html = html.replace(/\[ATTACH=JSON\](\{.*?\})\[\/ATTACH\]/gis, (match, jsonStr) => {
    try {
      const data = JSON.parse(jsonStr);
      if (data && data.data_attachmentid) {
        return `<img src="https://www.ahobilam.com/wp-content/uploads/forum/attach_${data.data_attachmentid}.jpg" alt="${data.title || 'Forum Attachment'}" class="max-w-full h-auto rounded-xl border border-[#D4AF37]/40 my-3 shadow-md inline-block max-h-[500px] object-contain" />`;
      }
      if (data && data['data-attachmentid']) {
        return `<img src="https://www.ahobilam.com/wp-content/uploads/forum/attach_${data['data-attachmentid']}.jpg" alt="${data.title || 'Forum Attachment'}" class="max-w-full h-auto rounded-xl border border-[#D4AF37]/40 my-3 shadow-md inline-block max-h-[500px] object-contain" />`;
      }
    } catch (e) {
      // Fallback regex if JSON parsing fails
      const idMatch = jsonStr.match(/attachmentid["']?:\s*(\d+)/i);
      if (idMatch) {
        return `<img src="https://www.ahobilam.com/wp-content/uploads/forum/attach_${idMatch[1]}.jpg" alt="Forum Attachment" class="max-w-full h-auto rounded-xl border border-[#D4AF37]/40 my-3 shadow-md inline-block max-h-[500px] object-contain" />`;
      }
    }
    return '';
  });

  // Standard BBCode tags & Image attachment tag
  html = html.replace(/\[ATTACH=CONFIG\](\d+)\[\/ATTACH\]/gis, '<img src="https://www.ahobilam.com/wp-content/uploads/forum/attach_$1.jpg" alt="Forum Attachment" class="max-w-full h-auto rounded-xl border border-[#D4AF37]/40 my-3 shadow-md inline-block max-h-[500px] object-contain" />');
  html = html.replace(/\[img\](.*?)\[\/img\]/gis, '<img src="$1" alt="Forum Attachment" class="max-w-full h-auto rounded-xl border border-[#D4AF37]/40 my-3 shadow-md inline-block max-h-[500px] object-contain" />');
  html = html.replace(/\[b\](.*?)\[\/b\]/gis, '<strong>$1</strong>');
  html = html.replace(/\[i\](.*?)\[\/i\]/gis, '<em>$1</em>');
  html = html.replace(/\[u\](.*?)\[\/u\]/gis, '<u>$1</u>');
  html = html.replace(/\[quote\](.*?)\[\/quote\]/gis, '<blockquote class="border-l-4 border-[#CC6600] pl-3 py-2 my-2 bg-[#FAF6EE] italic text-xs text-[#1A237E] rounded-r-lg shadow-2xs">$1</blockquote>');
  html = html.replace(/\[tks\](.*?)\[\/tks\]/gis, '<div class="p-3 my-3 bg-amber-50 border border-amber-300 rounded-xl text-amber-950 font-bold text-xs shadow-xs">🙏 $1</div>');
  html = html.replace(/\[url=([^\]]+)\](.*?)\[\/url\]/gis, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-[#CC6600] underline font-bold hover:text-[#E65100]">$2</a>');

  return html;
};

export const BBCodeRenderer = ({ content }) => {
  const parsed = renderFormattedBBCode(content);

  return (
    <div
      className="prose max-w-none text-xs sm:text-sm text-[#2D5A1E] leading-relaxed space-y-2"
      dangerouslySetInnerHTML={{ __html: parsed }}
    />
  );
};
